import ArrowBack from '@mui/icons-material/ArrowBack';
import ContentCopy from '@mui/icons-material/ContentCopy';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SaveIcon from '@mui/icons-material/Save';
import {
    Box,
    IconButton,
    Menu,
    MenuItem,
    Tooltip,
    Typography,
} from '@mui/material';
import React, { useState } from 'react';
import './DetailPageHeader.scss';

/**
 * The third page-header, kept deliberately, with one consumer.
 *
 * `PageHero` is the canonical one (14 call sites) and `CRMPageTemplate` and `EntityWorkspace`
 * carry their own. This is the survivor of the old detail-page shell, and the component-reuse
 * audit flagged it as the strongest deletion candidate in Common/. It is still here because the
 * parity check that CLAUDE.md requires before retiring a surface found two costs that outweigh
 * 217 lines of tidiness:
 *
 * - Its only consumer, `UserRBAC/UserCreateEditPopup`, uses `menuItems` for Delete User. PageHero
 *   has an `actions` slot but no overflow menu, so the conversion turns a menu item into a
 *   permanently visible destructive button on a create/edit page. That is a UX decision, not a
 *   refactor.
 * The second reason has since gone away and is recorded because it was wrong in an instructive
 * way: `legacy-form.scss` `@import`ed this component's stylesheet, and legacy-form was kept
 * because it looked like it redeclared classes `SEOMetadata` and `PopupLayout` own. Diffing them
 * showed otherwise — its `.gradient-text` was byte-identical to Panel's, its `.section-title` had
 * no consumers (the four apparent ones were `ma-`, `db-` and `notif-` prefixed classes a
 * substring grep had matched), and `SEOMetadata`'s copy is nested and therefore scoped. The whole
 * file was dead and is gone. This component's stylesheet is imported by this component, as it
 * always was.
 *
 * So: do not reach for this in new code, and if `/user-rbac` is ever reworked, it goes with it.
 * Left documented rather than deleted, because an undocumented orphan is what gets copied.
 */
export interface DetailPageMenuItem {
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
  color?: 'error' | 'warning';
  dividerBefore?: boolean;
}

export interface DetailPageHeaderProps {
  /** Main title — typically the entity name */
  title: string;
  /** Optional small subtitle below the title */
  subtitle?: string;
  /** Logo / avatar URL shown left of the actions */
  avatar?: string;
  /** Extra badge node next to the title (e.g. isExternal chip) */
  badge?: React.ReactNode;
  /** If provided, a copy-ID icon button is shown; clicking copies this value */
  entityId?: string;
  isEditMode: boolean;
  /** Show a spinner on the Save button */
  isSaving?: boolean;
  /** Hide Edit + Save if the page is view-only */
  canEdit?: boolean;
  /** If omitted, the back button is hidden */
  onBack?: () => void;
  onEdit: () => void;
  onSave: () => void;
  menuItems?: DetailPageMenuItem[];
  /** Slot for additional action buttons (e.g. Open in new tab) */
  rightActions?: React.ReactNode;
}

const DetailPageHeader: React.FC<DetailPageHeaderProps> = ({
  title,
  subtitle,
  avatar,
  badge,
  entityId,
  isEditMode,
  isSaving = false,
  canEdit = true,
  onBack,
  onEdit,
  onSave,
  menuItems = [],
  rightActions,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [copied, setCopied] = useState(false);

  const handleMenuOpen = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleCopyId = () => {
    if (entityId) {
      navigator.clipboard.writeText(entityId).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      });
    }
  };

  return (
    <div className="detail-page-header">
      <div className="detail-page-header__inner">
      {/* Left: Back + title */}
      <div className="detail-page-header__left">
        {onBack && (
          <Tooltip title="Go back">
            <IconButton
              className="detail-page-header__back-btn"
              size="small"
              onClick={onBack}
            >
              <ArrowBack sx={{ fontSize: 'var(--font-size-md)' }} />
            </IconButton>
          </Tooltip>
        )}

        <Box className="detail-page-header__title-wrap">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            <Typography className="detail-page-header__title" noWrap variant="h6">
              {title || '-'}
            </Typography>
            {badge}
          </Box>
          {subtitle && (
            <Typography className="detail-page-header__subtitle" noWrap variant="caption">
              {subtitle}
            </Typography>
          )}
        </Box>

        {entityId && (
          <Tooltip title={copied ? 'Copied!' : `Copy ID: ${entityId}`}>
            <IconButton
              className="detail-page-header__copy-btn"
              size="small"
              onClick={handleCopyId}
            >
              <ContentCopy sx={{ fontSize: 'var(--font-size-sm)' }} />
            </IconButton>
          </Tooltip>
        )}
      </div>

      {/* Right: actions */}
      <div className="detail-page-header__actions">
        {rightActions}

        {avatar && (
          <div className="detail-page-header__avatar">
            <img alt="logo" className="detail-page-header__avatar-img" src={avatar} />
          </div>
        )}

        {canEdit && isEditMode && (
          <>
            <button
              className="detail-page-header__cancel-btn"
              onClick={onEdit}
            >
              Cancel
            </button>
            <button
              className="detail-page-header__save-btn"
              disabled={isSaving}
              onClick={onSave}
            >
              <SaveIcon sx={{ fontSize: 14, mr: 0.5 }} />
              {isSaving ? 'Saving…' : 'Save'}
            </button>
          </>
        )}

        {menuItems.length > 0 && (
          <>
            <IconButton
              className={`detail-page-header__menu-btn${anchorEl ? ' active' : ''}`}
              size="small"
              onClick={handleMenuOpen}
            >
              <MoreVertIcon sx={{ fontSize: 18 }} />
            </IconButton>

            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              slotProps={{
                paper: {
                  sx: {
                    minWidth: 180,
                    borderRadius: 'var(--border-radius-md)',
                    boxShadow: 'var(--shadow-md)',
                    border: '1px solid var(--color-border)',
                    mt: 0.5,
                  },
                },
              }}
            >
              {menuItems.map((item, idx) => (
                <MenuItem
                  key={idx}
                  divider={item.dividerBefore}
                  sx={{
                    fontSize: 'var(--font-size-sm)',
                    gap: 1.5,
                    color: item.color === 'error'
                      ? 'var(--color-error)'
                      : item.color === 'warning'
                      ? 'var(--color-warning)'
                      : 'var(--color-text-primary)',
                    '&:hover': {
                      bgcolor:
                        item.color === 'error'
                          ? 'var(--red-tint)'
                          : item.color === 'warning'
                          ? 'var(--amber-tint)'
                          : 'var(--color-background-page)',
                    },
                  }}
                  onClick={() => { item.onClick(); handleMenuClose(); }}
                >
                  {item.icon && (
                    <Box sx={{ display: 'flex', alignItems: 'center', opacity: 0.8 }}>
                      {item.icon}
                    </Box>
                  )}
                  {item.label}
                </MenuItem>
              ))}
            </Menu>
          </>
        )}
      </div>
      </div>
    </div>
  );
};

export default DetailPageHeader;
